import { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Table,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "react-redux";

import {
  ListingRealVolumeKeysThunk,
  ListingRealVolumeValueThunk,
} from "../../Store/Thunks/ListingRealVolumeThunk";
import { ApiPutListingRealVolumeValue } from "../../Services";
import ReloadIcon from "../../Assets/img/icons/refresh.png";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode =
    inputType === "number" ? (
      <InputNumber
        style={{ width: 200 }}
        className="editable-field"
        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
      />
    ) : (
      <Input className="editable-field" />
    );
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item name={[record.key, dataIndex]} style={{ margin: 0 }}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const RealVolume = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [cellToggleState, setCellToggleState] = useState({});
  const [modifiedRows, setModifiedRows] = useState(new Set());
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkEditing, setBulkEditing] = useState(false);

  useEffect(() => {
    dispatch(ListingRealVolumeKeysThunk());
    dispatch(ListingRealVolumeValueThunk());
  }, []);

  const {
    ListingRealVolumeKeyData,
    ListingRealVolumeKeyLoading,
    ListingRealVolumeValueData,
    ListingRealVolumeValueLoading,
  } = useSelector((state) => state.ListingRealVolumeReducer);

  const isEditing = (record) => bulkEditing || record.key === editingKey;
  const edit = (record) => {
    form.setFieldsValue({
      [record.key]: { ...record },
    });
    setEditingKey(record.key);
  };
  const cancel = () => {
    setEditingKey("");
  };

  const dataSource = () => {
    const arr = [];
    ListingRealVolumeValueData.forEach((item, index) => {
      const obj = {};
      const volume = item.listingRealVolume ?? {};
      ListingRealVolumeKeyData.forEach((element) => {
        const key = element.key;
        const manualKey = `${key}Manual`;
        obj[element.key] = volume[element.key];
        obj.id = volume?.id;

        const manualDataExists = volume[manualKey] && volume[manualKey] != 0;
        obj[`${key}_automatic`] = volume[key];
        obj[key] = manualDataExists ? volume[manualKey] : volume[key];

        obj[`${key}_hasManualData`] = manualDataExists ? true : false;
        obj.id = volume.id;
      });

      arr.push({ name: item.assetName, ...obj, key: index });
    });
    setData(arr);
    setOriginalData(arr);
    return arr;
  };

  useEffect(() => {
    if (ListingRealVolumeKeyData?.length && ListingRealVolumeValueData.length) {
      dataSource();
    }
  }, [ListingRealVolumeKeyData, ListingRealVolumeValueData, cellToggleState]);

  const handleToggleCellData = async (rowId, colKey) => {
    const cellKey = `${rowId}_${colKey}`;
    setCellToggleState((prevState) => ({
      ...prevState,
      [cellKey]: !prevState[cellKey],
    }));

    // Prepare data to remove manual entry
    const row = data.find((item) => item.id === rowId);
    if (!row) return;

    const manualKey = `${colKey}Manual`;
    const newItem = { id: row.id, [manualKey]: null }; // Remove manual data

    // Call API to remove manual data
    setLoading(true);
    try {
      await ApiPutListingRealVolumeValue({ volumes: [newItem] });
      setModifiedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cellKey); // Remove the cell from modified rows after save
        return newSet;
      });

      // Fetch updated data from the backend
      dispatch(ListingRealVolumeKeysThunk());
      dispatch(ListingRealVolumeValueThunk());
    } catch (error) {
      console.error("Failed to update manual data:", error);
    } finally {
      setLoading(false);
    }
  };

  const editAssetValue = () => {
    setLoading(true);
    const changedData = data.filter((item, index) => {
      const originalItem = originalData[index];
      return Object.keys(item).some((key) => item[key] !== originalItem[key]);
    });
    const editData = Array.from(modifiedRows).reduce((acc, cellId) => {
      if (typeof cellId === "string") {
        const [rowId, colKey] = cellId.split("_");
        const row = data.find((item) => item.id === parseInt(rowId, 10));
        if (!row) return acc;

        // Find or initialize an item in acc for this row ID
        let newItem = acc.find((item) => item.id === row.id);
        if (!newItem) {
          newItem = { id: row.id };
          acc.push(newItem);
        }

        if (colKey === "sevenDayAverage") {
          newItem[`${colKey}Manual`] = row[colKey] ? String(row[colKey]) : "0";
        } else if (colKey === "ninetyDayAverage") {
          newItem[`${colKey}Manual`] = row[colKey] ? String(row[colKey]) : "0";
        } else if (colKey === "allTimePeakVolume") {
          newItem[`${colKey}`] = parseFloat(row[colKey]);
        } else {
          newItem[`${colKey}`] = String(row[colKey]);
        }
      }

      return acc;
    }, []);

    const payload = { volumes: editData };

    ApiPutListingRealVolumeValue(payload)
      .then(() => {
        setLoading(false);
        dispatch(ListingRealVolumeKeysThunk());
        dispatch(ListingRealVolumeValueThunk());
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const save = async (key) => {
    try {
      const values = await form.validateFields();
      const rowValues = values[key];
      if (!rowValues) return;

      setData((prev) =>
        prev.map((row) => (row.key === key ? { ...row, ...rowValues } : row)),
      );

      const row = data.find((r) => r.key === key);
      if (row) {
        Object.keys(rowValues).forEach((colKey) => {
          if (rowValues[colKey] !== row[colKey]) {
            setModifiedRows((prev) => new Set(prev).add(`${row.id}_${colKey}`));
          }
        });
      }

      setEditingKey("");
      setIsSaveClicked(true);
    } catch (err) {
      console.log("Validate Failed:", err);
    }
  };

  useEffect(() => {
    if (isSaveClicked) dispatch(ListingRealVolumeKeysThunk());
    dispatch(ListingRealVolumeValueThunk());
  }, [isSaveClicked]);

  useEffect(() => {
    if (modifiedRows && isSaveClicked) {
      editAssetValue();
    }
    setIsSaveClicked(false);
  }, [modifiedRows, isSaveClicked]);

  const columns = () => [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (text, record, index) => index + 1,
    },
    { title: "Asset", dataIndex: "name", fixed: "left" },
    ...ListingRealVolumeKeyData.filter(
      (item) =>
        item.key === "allTimePeakVolume" ||
        item.key === "sevenDayAverage" ||
        item.key === "ninetyDayAverage",
    ).map((item) => ({
      title: (
        <span>
          {item.key !== "allTimePeakVolume" ? "Transactions " : ""}
          {item.name}
        </span>
      ),
      dataIndex: item.key,
      editable: true,
      render: (text, record) => {
        if (!record) return null;

        const hasManualData = record[`${item.key}_hasManualData`];
        const automaticValue = record[`${item.key}_automatic`];

        const showAutomaticSymbol =
          hasManualData &&
          automaticValue !== null &&
          automaticValue !== undefined &&
          automaticValue !== "";
        return (
          <span style={{ position: "relative", display: "block" }}>
            <span style={{ color: hasManualData ? "#1890ff" : "#000" }}>
              {text
                ? Number(text)
                    ?.toFixed(0)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : ""}
            </span>

            {/* Show "A" only if both manual and automatic values exist */}
            {showAutomaticSymbol ? (
              <span
                className="has_automatic"
                title={Number(automaticValue)
                  ?.toFixed(0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              >
                A
              </span>
            ) : (
              ""
            )}

            {/* Show reload icon if manual data exists */}
            {hasManualData && (
              <img
                src={ReloadIcon}
                alt="Reload"
                style={{
                  width: "20px",
                  position: "absolute",
                  top: "50%",
                  right: "10px",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() => handleToggleCellData(record.id, item.key)}
              />
            )}
          </span>
        );
      },
    })),
    {
      title: "Operation",
      dataIndex: "operation",
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable && !bulkEditing ? (
          <span>
            <Typography.Link
              onClick={() => save(record.key)}
              style={{ marginRight: 8 }}
            >
              Save
            </Typography.Link>
            <Popconfirm
              title="Sure to cancel?"
              okText="Cancel"
              cancelText="No"
              okButtonProps={{ style: { float: "right" } }}
              cancelButtonProps={{ style: { float: "left" } }}
              onConfirm={cancel}
              overlayStyle={{ paddingBottom: "20px" }}
            >
              <a>Cancel</a>
            </Popconfirm>
          </span>
        ) : !bulkEditing ? (
          <Typography.Link
            disabled={editingKey !== ""}
            onClick={() => edit(record)}
          >
            Edit
          </Typography.Link>
        ) : null;
      },
    },
  ];

  const mergedColumns = columns().map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: editingKey !== "" && !selectedRowKeys.includes(record.key),
    }),
  };

  const bulkEdit = () => {
    const values = {};
    data.forEach((row) => {
      if (selectedRowKeys.includes(row.key)) {
        values[row.key] = row;
      }
    });
    form.setFieldsValue(values);
    setBulkEditing(true);
  };
  const bulkSave = async () => {
    try {
      const values = await form.validateFields();

      setData((prev) =>
        prev.map((row) => {
          if (!values[row.key]) return row;

          Object.keys(values[row.key]).forEach((colKey) => {
            setModifiedRows((prevSet) =>
              new Set(prevSet).add(`${row.id}_${colKey}`),
            );
          });

          return { ...row, ...values[row.key] };
        }),
      );

      setBulkEditing(false);
      setSelectedRowKeys([]);
      setIsSaveClicked(true);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div>
      <Form form={form} component={false}>
        <Table
          rowSelection={rowSelection}
          loading={
            ListingRealVolumeKeyLoading ||
            ListingRealVolumeValueLoading ||
            loading
          }
          components={{ body: { cell: EditableCell } }}
          pagination={false}
          bordered
          dataSource={data}
          className="custom-scroll"
          columns={mergedColumns}
          rowClassName="editable-row"
          scroll={{
            y: 500,
          }}
        />
      </Form>
      <div style={{ marginTop: 20 }}>
        <Button
          disabled={!selectedRowKeys.length || editingKey !== ""}
          onClick={bulkEdit}
        >
          Bulk Edit
        </Button>

        {bulkEditing && (
          <>
            <Button type="primary" style={{ marginLeft: 8 }} onClick={bulkSave}>
              Bulk Save
            </Button>

            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setBulkEditing(false);
                setSelectedRowKeys([]);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
